import { useContext, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import api from '../../api';
import IdentityContext from '../../contexts/IdentityContext';
import InvitationContext from '../../contexts/InvitationContext';
import { useRoutes } from '../../hooks/useRoutes';

export enum GroupInviteState {
  Success = 'Success',
  Failed = 'Failed',
}

export enum GroupInviteMessage {
  AlreadyAccepted = 'AlreadyAccepted',
  NonStudent = 'NonStudent',
  LinkDeprecated = 'LinkDeprecated',
  TooManyMembers = 'TooManyMembers',
  GeneralError = 'GeneralError',
}

const GroupInvite = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { buildAbsolutePath } = useRoutes();

  const { identity } = useContext(IdentityContext);
  const { fetchInvitations } = useContext(InvitationContext);

  useEffect(() => {
    const handleGroupInvite = async (): Promise<void> => {
      if (!id) return;

      let state,
        message,
        retryUrl = undefined;

      try {
        const res = await api.post(`/studio/user/groups/${id}/join`);

        if (res.status === 200) {
          await fetchInvitations();
          return navigate(buildAbsolutePath(['MY_TEACHERS']));
        }

        if (res.status === 204) {
          state = GroupInviteState.Success;
          message = GroupInviteMessage.AlreadyAccepted;
        }
      } catch (err) {
        state = GroupInviteState.Failed;
        switch ((err as AxiosError).response?.status) {
          case 403:
            message = GroupInviteMessage.NonStudent;

            return navigate(buildAbsolutePath(['MY_METHODS', 'MY_METHODS.DASHBOARD']), {
              state: {
                groupInviteModal: {
                  state,
                  message,
                },
              },
            });

          case 404:
            message = GroupInviteMessage.LinkDeprecated;
            break;
          case 409:
            message = GroupInviteMessage.TooManyMembers;
            break;
          default:
            message = GroupInviteMessage.GeneralError;
            retryUrl = buildAbsolutePath(['INVITE'], { id });
        }
      }

      return navigate(buildAbsolutePath(['MY_TEACHERS']), {
        state: {
          groupInviteModal: {
            state,
            message,
            retryUrl,
          },
        },
      });
    };

    handleGroupInvite();
  }, [id, navigate, identity, location, fetchInvitations, buildAbsolutePath]);

  return null;
};

export default GroupInvite;
